import { useEditor, EditorContent } from '@tiptap/react';
import classNames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createEditorActions } from './ContentEditor.actions';
import { createEditorConfig } from './ContentEditor.config';
import styles from './ContentEditor.module.scss';
import { ContentEditorToolbar } from './ContentEditorToolbar';

type Props = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
};

export const ContentEditor = ({ value, onChange, placeholder = '' }: Props) => {
  const { t } = useTranslation();
  const [isEmpty, setIsEmpty] = useState(!value || value === '<p></p>' || value === '');

  const config = createEditorConfig(placeholder);

  const editor = useEditor({
    ...config,
    content: isEmpty ? placeholder : value,
    onUpdate: ({ editor }) => {
      const content = editor.getHTML();
      const contentIsEmpty = !content || content === '<p></p>';

      if (contentIsEmpty) {
        onChange(placeholder);
        setIsEmpty(true);
      } else {
        onChange(content);
        setIsEmpty(false);
      }
    },
    onFocus: ({ editor }) => {
      if (isEmpty) {
        editor.commands.setContent('');
      }
    },
    onBlur: ({ editor }) => {
      if (editor.isEmpty) {
        editor.commands.setContent(placeholder);
        setIsEmpty(true);
        onChange(placeholder);
      }
    },
  });

  const actions = editor ? createEditorActions(editor, t) : [];

  const handleEditorClick = () => {
    if (isEmpty && editor) {
      editor.commands.setContent('');
      setIsEmpty(false);
    }
  };

  return (
    <div className="p-0">
      <div id="hs-editor-tiptap">
        <ContentEditorToolbar actions={actions} />
        <div
          className={classNames(
            'mt-4 min-h-[280px] w-full border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-0 p-0 focus-within:!ring-[3px] focus-within:ring-ring/25 focus-within:!border-foreground',
            { 'text-foreground-subtle': isEmpty },
          )}
        >
          <EditorContent
            editor={editor}
            className={classNames('h-full resize-none leading-3 text-sm', styles.richtextContent)}
            onClick={handleEditorClick}
          />
        </div>
      </div>
    </div>
  );
};
